import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import { get, isEmpty, keyBy, isEqual } from 'lodash';
import { computeUserLearningAssetCompleteness, canCalculateCompleteness } from '../utils/UserLearningAssetUtils';
import { LICENSE_TYPES } from '../Constants';
import log from '../utils/log';

const TYPE_IS_SKILLS = 'isSkills';
const TYPE_IS_IMPACT = 'isImpact';

// selector that uses lodash.isEqual instead of ===
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export const makeSegmentSelector = (stateKey, type, id) => {
    if (id !== '' && id !== null) {
        return createSelector(
            [(state) => get(state, `${stateKey}.segments.${type}.${id}`), (state) => state[stateKey].byId],
            (segment = [], byId) => segment.map((id) => byId[id])
        );
    }

    return null;
};

export const makeGetObjectsFromStateByFunction = (stateKey) => {
    if (isEmpty(stateKey)) {
        throw new Error('stateKey is a required property');
    }
    return createSelector(
        [(state) => get(state, `${stateKey}`, {}), (_, filterFunc) => filterFunc],
        (byIdLookUp, filterFunc) => Object.values(byIdLookUp).filter((item) => filterFunc(item))
    );
};

export const makeGetObjectByKeyArrays = (stateKey) => {
    if (isEmpty(stateKey)) {
        throw new Error('stateKey is a required property');
    }
    return createSelector(
        [(state) => get(state, `${stateKey}`, {}), (_, propertyKey) => propertyKey, (_, __, key) => key],
        (byIdLookUp, propertyKey, key) =>
            Object.values(get(byIdLookUp, propertyKey, {})).reduce((acc, item) => {
                if (!item[key]) {
                    return acc;
                }
                if (acc[item[key]]) {
                    acc[item[key]].push(item);
                } else {
                    acc[item[key]] = [item];
                }
                return acc;
            }, {})
    );
};

export const makeGetObjectsFromStateByProperty = (stateKey) => {
    if (isEmpty(stateKey)) {
        throw new Error('stateKey is a required property');
    }

    return createSelector(
        [
            (state) => state && state[stateKey] && (state[stateKey].byId || {}),
            (_, property) => property,
            (_, __, value) => value
        ],
        (byIdLookup, property, value) => Object.values(byIdLookup).filter((item) => item && item[property] === value)
    );
};

export const makeGetObjectDictFromStateByProperty = (stateKey) => {
    if (isEmpty(stateKey)) {
        throw new Error('stateKey is a required property');
    }

    return createSelector(
        [
            (state) => state && state[stateKey] && (state[stateKey].byId || {}),
            (_, property) => property,
            (_, __, value) => value,
            (_, __, ___, lookupKey) => lookupKey
        ],
        (byIdLookup, property, value, lookupKey) =>
            Object.values(byIdLookup).reduce((acc, item) => {
                if (item && item[property] === value && item[lookupKey]) {
                    acc[item[lookupKey]] = item;
                }
                return acc;
            }, {})
    );
};

export const makeGetFilteredObjectsFromState = (stateKey) => {
    if (isEmpty(stateKey)) {
        throw new Error('stateKey is a required property');
    }

    return createSelector(
        [(state) => state && state[stateKey] && (state[stateKey].byId || {}), (_, predicateFunc) => predicateFunc],
        (byIdLookup, predicateFunc) => Object.values(byIdLookup || {}).filter((item) => predicateFunc(item) === true)
    );
};

export const makeGetFilteredObjectsByIdFromState = (stateKey) => {
    if (isEmpty(stateKey)) {
        throw new Error('stateKey is a required property');
    }

    return createSelector(
        [(state) => state && state[stateKey] && (state[stateKey].byId || {}), (_, predicateFunc) => predicateFunc],
        (byIdLookup, predicateFunc) =>
            Object.keys(byIdLookup || {}).reduce((acc, key) => {
                if (predicateFunc(byIdLookup[key])) {
                    acc[key] = byIdLookup[key];
                }
                return acc;
            }, {})
    );
};

export const makeGetObjectsFromStateById = (stateKey) => {
    if (isEmpty(stateKey)) {
        throw new Error('stateKey is a required property');
    }

    return createSelector([(state) => get(state, `${stateKey}.byId`, {})], (byIdLookup) => Object.values(byIdLookup));
};

export const makeGetObjectsFromStateLookup = (stateKey, lookupKey) => {
    if (isEmpty(stateKey)) {
        throw new Error('stateKey is a required property');
    }

    if (isEmpty(lookupKey)) {
        throw new Error('lookupKey is a required');
    }

    return createSelector([(state) => get(state, `${stateKey}.${lookupKey}`, {})], (byIdLookup) =>
        Object.values(byIdLookup)
    );
};

export const makeFilterByDictionary = (filterKey) =>
    createSelector([(array) => array, (_, dict) => dict], (array, dict) => {
        if (!array) {
            return [];
        }
        if (!dict) {
            throw new Error('must pass an object as second argument');
        }

        if (filterKey) {
            return array.filter((item) => {
                if (item) {
                    return !dict[item[filterKey]];
                }
                return false;
            });
        }
        return array.filter((item) => !dict[item]);
    });

export const makeGetUserLearningAssetsByTypeForUser = () =>
    createDeepEqualSelector(
        [
            (state, userProfileId) => get(state, `userLearningAssets.segments.users['${userProfileId}']`, []),
            (state) => get(state, 'userLearningAssets.byId', {}),
            (state, _, type) => type
        ],
        (userLearningAssetIdsByUser, userLearningAssetById, type) => {
            if (isEmpty(userLearningAssetIdsByUser)) {
                return [];
            }

            const results = [];

            let userLearningAssetToAdd = {};
            userLearningAssetIdsByUser.forEach((userLearningAssetId) => {
                if (!(userLearningAssetId in userLearningAssetById)) {
                    return;
                }

                userLearningAssetToAdd = userLearningAssetById[userLearningAssetId];
                if (type === userLearningAssetToAdd.type || isEmpty(type)) {
                    userLearningAssetToAdd.isUserLearningAsset = true;

                    if (get(userLearningAssetToAdd, 'isCompleted')) {
                        userLearningAssetToAdd.completeness = 100;
                    } else if (canCalculateCompleteness(userLearningAssetToAdd)) {
                        userLearningAssetToAdd.completeness =
                            computeUserLearningAssetCompleteness(userLearningAssetToAdd);
                    } else {
                        userLearningAssetToAdd.completeness = 0;
                    }

                    results.push(userLearningAssetToAdd);
                }
            });
            return results;
        }
    );

/**
 *
 * @param state
 * @param objectId
 * @param {String} type
 * @param {String} userId
 */
export const lookupObjectIdByUser = (state, objectId, type, userId) =>
    get(state.userEventsData, `segments.organized.${userId}.${type}.byObjectId.${objectId}`);

/**
 * Looks like a UserLearningAsset by LearningAssetId and UserProfileId
 * @return {*}
 */
export const makeGetUserLearningAssetByParams = () =>
    createSelector(
        [
            (state) => state.userLearningAssets.byId,
            (_, props) => props.userProfileId,
            (_, props) => props.learningAssetId
        ],
        (userLearningAssetsById, userProfileId, learningAssetId) => {
            if (!userLearningAssetsById) {
                return null;
            }
            const values = Object.values(userLearningAssetsById);

            let currentUserTrack;
            for (let userTrackIndex = 0; userTrackIndex < values.length; userTrackIndex += 1) {
                currentUserTrack = values[userTrackIndex];
                if (currentUserTrack.userProfileId === userProfileId && currentUserTrack.trackId === learningAssetId) {
                    return currentUserTrack;
                }
            }

            return null;
        }
    );

export const makeGetFilesForUserTrack = () =>
    createSelector(
        [
            (state) => state.userLearningAssets.byId,
            (state) => state.files,
            (_, userLearningAssetId) => userLearningAssetId
        ],
        (userTracksById, fileSegments, userLearningAssetId) => {
            if (isEmpty(userTracksById) || isEmpty(fileSegments)) {
                return {};
            }

            // get userTrack
            const currentUserTrack = userTracksById[userLearningAssetId];

            if (currentUserTrack) {
                const fileList = get(fileSegments, `['UserTrack-${userLearningAssetId}']`, {});
                return fileList;
            }

            return {};
        }
    );

export const makeGetCollectionContent = () =>
    createSelector(
        [(collection) => collection, (_, state) => get(state, 'learningAssets.byId', {})],
        (collection, learningAssetsById) => {
            if (!collection || isEmpty(learningAssetsById)) {
                return [];
            }
            const assetsToReturn = [];
            get(collection, 'contentIds', []).forEach((asset) => {
                const assetToReturn = get(learningAssetsById, asset.id, {});
                if (!isEmpty(assetToReturn)) {
                    assetsToReturn.push(assetToReturn);
                }
            });
            return assetsToReturn;
        }
    );

export const makeGetUserCollectionByUser = () =>
    createSelector(
        [(state) => get(state, `userCollections.byId`, {}), (_, userProfileId) => userProfileId],
        (byIdLookup, userProfileId) =>
            Object.values(byIdLookup)
                .filter((collection) => collection.userProfileId === userProfileId)
    );

export const makeGetUserCollectionContent = () =>
    createSelector(
        [(userCollection) => userCollection, (_, state) => get(state, 'userLearningAssets.byId', {})],
        (userCollection, userLearningAssetsById) => {
            if (isEmpty(userCollection) || isEmpty(userLearningAssetsById)) {
                return [];
            }

            const userCollectionContent = get(userCollection, 'content', []);
            let assetToReturn;
            let currentId;

            return userCollectionContent.map((userLearningAsset) => {
                currentId = userLearningAsset.userLearningAssetId;
                assetToReturn = get(userLearningAssetsById, currentId, {});

                if (assetToReturn.isCompleted) {
                    assetToReturn.completeness = 100;
                } else if (canCalculateCompleteness(assetToReturn)) {
                    assetToReturn.completeness = computeUserLearningAssetCompleteness(assetToReturn);
                } else {
                    assetToReturn.completeness = 0;
                }

                return assetToReturn;
            });
        }
    );

export const makeGetUserPathwayByUser = () =>
    createSelector(
        [(state) => get(state, `userPathways.byId`, {}), (_, userProfileId) => userProfileId],
        (byIdLookup, userProfileId) =>
            Object.values(byIdLookup).filter((pathway) => pathway.userProfileId === userProfileId)
    );

/**
 * THIS NEEDS TO GO AWAY...WE SHOULD NOT BE INTRODUCING SIDE EFFECTS INTO PASSED IN DATA!!!
 *
 * @return {*}
 */
export const makeAvailableLearningAssets = () =>
    createSelector(
        [
            (state) => get(state, 'companies.byId', {}),
            (state) => get(state, 'auth.loggedInUser', {}),
            (_, learningAssets) => learningAssets,
            (_, __, userLearningAssets) => userLearningAssets
        ],
        (companies, loggedInUser, learningAssets, userLearningAssets) => {
            if (!learningAssets || !learningAssets.length) {
                return [];
            }

            const userCompanyId = get(loggedInUser, 'companyId', '');
            const userCompany = get(companies, userCompanyId, {});
            const companyTrackLicenses = get(userCompany, 'trackLicenses', []);

            const dictionary = userLearningAssets.reduce((acc, asset) => {
                acc[asset.trackId] = true;
                return acc;
            }, {});

            return learningAssets.filter((asset) => {
                const currentAssetId = asset._id;

                if (asset.subscriptionType === LICENSE_TYPES.NOT_AVAILABLE) {
                    // if not available, don't include
                    return false;
                }
                if (asset.subscriptionType === LICENSE_TYPES.EXCLUSIVE_COMPANY_LICENSE) {
                    // if has license, include, otherwise don't
                    return companyTrackLicenses.indexOf(currentAssetId) > -1;
                }
                if (dictionary[asset._id]) {
                    // include a user's learning asset
                    return true;
                }
                return true;
            });
        }
    );

/**
 *
 * @return {*}
 */
export const makeGetLearningAssetsByType = () =>
    createSelector(
        [
            (state) => get(state, 'learningAssets.byId', {}),
            (_, userLearningAssets) => userLearningAssets,
            (_, __, type) => type
        ],
        (learningAssets, userLearningAssets, isType) => {
            if (isType !== TYPE_IS_SKILLS && isType !== TYPE_IS_IMPACT) {
                log.error(
                    `isType is not one of the expected Types. [Expected: ${TYPE_IS_SKILLS}, ${TYPE_IS_IMPACT}] [Actual: ${isType}]`
                );
                return [];
            }

            if (isEmpty(learningAssets)) {
                return [];
            }

            const dictionary = userLearningAssets.reduce((acc, asset) => {
                acc[asset.trackId] = true;
                return acc;
            }, {});

            return Object.values(learningAssets).filter((asset) => {
                if (dictionary[asset._id]) {
                    return false;
                }
                if (!asset[isType]) {
                    return false;
                }
                return true;
            });
        }
    );

export const makeGetAvailableUnsubscribedLearningAssetsByType = () =>
    createSelector(
        [
            (state) => get(state, 'learningAssets.byId', {}),
            (_, userLearningAssets) => userLearningAssets,
            (_, __, type) => type,
            (state, __, ___, companyId) => get(state, `companies.byId.${companyId}.trackLicenses`, [])
        ],
        (learningAssets, userLearningAssets, isType, trackLicenses) => {
            if (isType !== TYPE_IS_SKILLS && isType !== TYPE_IS_IMPACT) {
                log.error(
                    `isType is not one of the expected Types. [Expected: ${TYPE_IS_SKILLS}, ${TYPE_IS_IMPACT}] [Actual: ${isType}]`
                );
                return [];
            }

            if (isEmpty(learningAssets)) {
                return [];
            }

            const userLearningAssetsDictionary = userLearningAssets.reduce((acc, asset) => {
                acc[asset.trackId] = true;
                return acc;
            }, {});

            const companyTrackLicensesDictionary = trackLicenses.reduce((acc, license) => {
                acc[license.trackId] = true;
                return acc;
            }, {});

            return Object.values(learningAssets).filter((asset) => {
                if (userLearningAssetsDictionary[asset._id]) {
                    return false;
                }
                if (!asset[isType]) {
                    return false;
                }
                if (asset.subscriptionType === 'AvailableForAll') {
                    return true;
                }
                if (asset.subscriptionType === 'ExclusiveCompanyLicense' && companyTrackLicensesDictionary[asset._id]) {
                    return true;
                }
                return false;
            });
        }
    );

export const makeAvailableLearningAssetsNoUserAssets = () =>
    createSelector(
        [
            (state) => get(state, 'companies.byId', {}),
            (state) => get(state, 'auth.loggedInUser', {}),
            (_, learningAssets) => learningAssets,
            (_, __, userLearningAssets) => userLearningAssets
        ],
        (companies, loggedInUser, learningAssets, userLearningAssets) => {
            if (!learningAssets || !learningAssets.length) {
                return [];
            }

            const userCompanyId = get(loggedInUser, 'companyId', '');
            const userCompany = get(companies, userCompanyId, {});
            const companyTrackLicenses = get(userCompany, 'trackLicenses', []);

            const dictionary = userLearningAssets.reduce((acc, asset) => {
                acc[asset.trackId] = true;
                return acc;
            }, {});

            return learningAssets.filter((asset) => {
                const currentAssetId = asset._id;

                if (asset.subscriptionType === LICENSE_TYPES.NOT_AVAILABLE) {
                    // if not available, don't include
                    return false;
                }
                if (dictionary[asset._id]) {
                    // include a user's learning asset
                    return false;
                }
                if (asset.subscriptionType === LICENSE_TYPES.EXCLUSIVE_COMPANY_LICENSE) {
                    // if has license, include, otherwise don't
                    return companyTrackLicenses.indexOf(currentAssetId) > -1;
                }
                return true;
            });
        }
    );

const learningAssetForCompany = (companiesById, learningAssetsById, companyId) => {
    if (isEmpty(learningAssetsById) || isEmpty(companyId) || isEmpty(companiesById)) {
        return [];
    }

    const company = companiesById[companyId];
    if (!company) {
        return [];
    }
    const courseLicenses = get(company, 'trackLicenses', []);
    const courseLicenseLookup = keyBy(courseLicenses, 'trackId');

    return Object.values(learningAssetsById).reduce((acc, learningAsset) => {
        const subscriptionType = get(learningAsset, 'subscriptionType', '').toLocaleUpperCase();
        const hasLicense = learningAsset._id in courseLicenseLookup;
        const doesntNeedLicense = subscriptionType === LICENSE_TYPES.AVAILABLE_FOR_ALL_UPPER;
        if (doesntNeedLicense || hasLicense) {
            acc.push(learningAsset);
        }
        return acc;
    }, []);
};

export const makeGetCompanyLearningAssets = () =>
    createSelector(
        [
            (state) => get(state, 'companies.byId', {}),
            (state) => get(state, 'learningAssets.byId', {}),
            (_, props) => get(props, 'match.params.id', '')
        ],
        learningAssetForCompany
    );

export const makeGetCompanyLearningAssetsForId = (state) =>
    createSelector(
        [() => get(state, 'companies.byId', {}), () => get(state, 'learningAssets.byId', {}), (companyId) => companyId],
        learningAssetForCompany
    );

export const makeGetByIdLookupForLearningAssets = (type) =>
    createSelector(
        [(state) => get(state, 'learningAssets.byId', {}), (state) => get(state, `learningAssets['${type}']`, {})],
        (learningAssetsById, typesById) => {
            if (isEmpty(learningAssetsById) || isEmpty(typesById)) {
                return {};
            }

            return Object.keys(typesById).reduce((typesToReturn, learningAssetId) => {
                if (learningAssetId in learningAssetsById) {
                    typesToReturn[learningAssetId] = learningAssetsById[learningAssetId];
                }
                return typesToReturn;
            }, {});
        }
    );

export const makeGetCompanyLearningAssetsByPermalink = () =>
    createSelector(
        [
            (state) => get(state, 'companies.byPermalink', {}),
            (state) => get(state, 'learningAssets.byId', {}),
            (_, props) => get(props, 'match.params.permalink', '')
        ],
        (companiesByPerma, learningAssetsById, companyPermalink) => {
            if (isEmpty(learningAssetsById) || isEmpty(companyPermalink)) {
                return [];
            }

            const company = get(companiesByPerma, `[${companyPermalink}]`, {});
            const courseLicenses = get(company, 'trackLicenses', []);
            const courseLicenseLookup = keyBy(courseLicenses, 'trackId');

            return Object.values(learningAssetsById).reduce((acc, learningAsset) => {
                const subscriptionType = get(learningAsset, 'subscriptionType', '').toLocaleUpperCase();
                const hasLicense = learningAsset._id in courseLicenseLookup;
                const doesntNeedLicense = subscriptionType === LICENSE_TYPES.AVAILABLE_FOR_ALL_UPPER;
                if (doesntNeedLicense || hasLicense) {
                    acc.push(learningAsset);
                }
                return acc;
            }, []);
        }
    );

export const makeTableBySortOrder = (assets) =>
    createSelector(
        [(state) => get(state, `${assets}.filteredById`, {}), (_, sortOrder) => sortOrder || []],
        (assetsById, sortOrder) => {
            if (isEmpty(assetsById)) {
                return [];
            }
            return sortOrder.reduce((acc, assetId) => {
                if (assetsById[assetId]) {
                    acc.push(assetsById[assetId]);
                }
                return acc;
            }, []);
        }
    );
